<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AccessToken from './AccessToken.vue';
import PasskeySettingsCard from './PasskeySettingsCard.vue';
import ActiveSessions from './ActiveSessions.vue';
import Policy from 'dashboard/components/policy.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import HotKeySvg from './HotKeySvg.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { CONTEXTUAL_HELP_KEYS } from 'dashboard/constants/contextualHelp';

export default {
  components: {
    MessageSignature,
    SectionLayout,
    FontSize,
    UserLanguageSelect,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    RadioCard,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    AccessToken,
    PasskeySettingsCard,
    ActiveSessions,
    BaseSettingsHeader,
    ToggleSwitch,
    HotKeySvg,
  },
  setup() {
    const { isEditorHotKeyEnabled, uiSettings, updateUISettings } =
      useUISettings();
    const { currentFontSize, updateFontSize } = useFontSize();
    const { replaceInstallationName } = useBranding();

    return {
      currentFontSize,
      updateFontSize,
      uiSettings,
      isEditorHotKeyEnabled,
      updateUISettings,
      replaceInstallationName,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      name: '',
      displayName: '',
      email: '',
      messageSignature: '',
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
          darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
          darkImage:
            '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    apiAndWebhooksEnabled() {
      if (!this.isOnChatwootCloud) return true;

      return this.currentUser.accounts.some(
        account => account.api_and_webhooks
      );
    },
    accessTokenDescription() {
      if (!this.apiAndWebhooksEnabled) {
        return this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.PAID_PLAN_NOTE');
      }

      return this.replaceInstallationName(
        this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE')
      );
    },
    isWelcomeTourAutoBlocked: {
      get() {
        return (
          !!this.uiSettings?.welcome_tour_auto_blocked ||
          !!this.uiSettings?.disable_all_tours ||
          !!this.uiSettings?.dashboard_tour_completed
        );
      },
      set(value) {
        this.updateUISettings({
          welcome_tour_auto_blocked: value,
          dashboard_tour_completed: value,
          onboarding_completed: value,
          disable_all_tours: false,
        });
        const message = value
          ? this.$t(
              'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.WELCOME.AUTO_BLOCKED'
            )
          : this.$t(
              'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.WELCOME.AUTO_ALLOWED'
            );
        useAlert(message);
      },
    },
    isContextualToursAutoBlocked: {
      get() {
        return (
          !!this.uiSettings?.contextual_tours_auto_blocked ||
          !!this.uiSettings?.disable_all_tours
        );
      },
      set(value) {
        this.updateUISettings({
          contextual_tours_auto_blocked: value,
          disable_all_tours: false,
        });
        const message = value
          ? this.$t(
              'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.CONTEXTUAL.AUTO_BLOCKED'
            )
          : this.$t(
              'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.CONTEXTUAL.AUTO_ALLOWED'
            );
        useAlert(message);
      },
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.messageSignature = this.currentUser.message_signature;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage);
      }
    },
    restartProductTour() {
      this.updateUISettings({
        dashboard_tour_completed: false,
        onboarding_completed: false,
      });
      this.$router.push({
        name: 'home',
        params: { accountId: this.$route.params.accountId },
        query: { product_tour: CONTEXTUAL_HELP_KEYS.DASHBOARD_INBOX },
      });
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName } = userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;
      this.name = name || this.name;
      this.email = email || this.email;
      this.displayName = displayName || this.displayName;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
        avatar: this.avatarFile,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
    },
    async onCopyToken(value) {
      if (!this.apiAndWebhooksEnabled) return;

      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    async resetAccessToken() {
      if (!this.apiAndWebhooksEnabled) return;

      const success = await this.$store.dispatch('resetAccessToken');
      if (success) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS'));
      } else {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="grid w-full min-w-0">
    <BaseSettingsHeader :title="$t('PROFILE_SETTINGS.TITLE')" description="" />
    <SectionLayout title="" description="" class="!pt-0">
      <div class="flex flex-col gap-6">
        <UserProfilePicture
          :src="avatarUrl"
          :name="name"
          @change="updateProfilePicture"
          @delete="deleteProfilePicture"
        />
        <UserBasicDetails
          :name="name"
          :display-name="displayName"
          :email="email"
          :email-enabled="!globalConfig.disableUserProfileUpdate"
          @update-user="updateProfile"
        />
      </div>
    </SectionLayout>
    <SectionLayout
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
      :description="
        replaceInstallationName(
          $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
        )
      "
    >
      <div class="flex flex-col gap-6 items-start">
        <FontSize
          :value="currentFontSize"
          :label="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
          "
          @change="updateFontSize"
        />
        <UserLanguageSelect
          :label="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
          "
        />
        <div class="flex w-full flex-col gap-4 border-t border-n-weak pt-6">
          <div class="flex flex-col gap-1">
            <div class="flex min-w-0 flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.TITLE'
                  )
                }}
              </label>
              <p class="text-sm text-n-slate-11">
                {{
                  $t(
                    'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.NOTE'
                  )
                }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center justify-between gap-4 border-t border-n-weak pt-4"
          >
            <div class="flex min-w-0 flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.WELCOME.TITLE'
                  )
                }}
              </span>
              <p class="text-sm text-n-slate-11">
                {{
                  $t(
                    'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.WELCOME.NOTE'
                  )
                }}
              </p>
            </div>
            <ToggleSwitch v-model="isWelcomeTourAutoBlocked" />
          </div>
          <div
            class="flex items-center justify-between gap-4 border-t border-n-weak pt-4"
          >
            <div class="flex min-w-0 flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.CONTEXTUAL.TITLE'
                  )
                }}
              </span>
              <p class="text-sm text-n-slate-11">
                {{
                  $t(
                    'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.CONTEXTUAL.NOTE'
                  )
                }}
              </p>
            </div>
            <ToggleSwitch v-model="isContextualToursAutoBlocked" />
          </div>
          <div
            class="flex items-center justify-between gap-3 border-t border-n-weak pt-4"
          >
            <span class="text-sm text-n-slate-11">
              {{
                $t(
                  'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.PRODUCT_TOURS.WELCOME.RESTART_NOTE'
                )
              }}
            </span>
            <button
              type="button"
              class="shrink-0 px-3 py-1.5 text-sm font-medium rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-12 cursor-pointer transition-all duration-100 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
              @click="restartProductTour"
            >
              {{ $t('PRODUCT_TOUR.RESTART_TOUR') }}
            </button>
          </div>
        </div>
      </div>
    </SectionLayout>
    <SectionLayout
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')"
    >
      <MessageSignature
        :message-signature="messageSignature"
        @update-signature="updateSignature"
      />
    </SectionLayout>
    <SectionLayout
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
    >
      <div
        class="flex flex-col justify-between w-full gap-5 sm:gap-4 sm:flex-row"
      >
        <RadioCard
          v-for="hotKey in hotKeys"
          :id="hotKey.key"
          :key="hotKey.key"
          :label="hotKey.title"
          :description="hotKey.description"
          :is-active="isEditorHotKeyEnabled(hotKey.key)"
          class="sm:flex-1"
          @select="toggleHotKey"
        >
          <HotKeySvg :hot-key="hotKey.key" />
        </RadioCard>
      </div>
    </SectionLayout>
    <SectionLayout
      v-if="!globalConfig.disableUserProfileUpdate"
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
      description=""
    >
      <ChangePassword />
    </SectionLayout>
    <SectionLayout
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.NOTE')"
    >
      <PasskeySettingsCard />
    </SectionLayout>
    <SectionLayout
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.NOTE')"
    >
      <ActiveSessions />
    </SectionLayout>
    <Policy :permissions="audioNotificationPermissions">
      <SectionLayout
        with-border
        :title="$t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')"
        :description="
          $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
        "
      >
        <AudioNotifications />
      </SectionLayout>
    </Policy>
    <Policy :permissions="notificationPermissions">
      <SectionLayout
        with-border
        :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')"
        description=""
      >
        <NotificationPreferences />
      </SectionLayout>
    </Policy>
    <SectionLayout
      with-border
      :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
      :description="accessTokenDescription"
    >
      <AccessToken
        :value="currentUser.access_token"
        :disabled="!apiAndWebhooksEnabled"
        @on-copy="onCopyToken"
        @on-reset="resetAccessToken"
      />
    </SectionLayout>
  </div>
</template>
